import type {
  CodeableConcept,
  Observation,
  ObservationComponent,
  Quantity,
} from '@medplum/fhirtypes';

import { type ChoiceValue, getChoiceValue } from '../../resolvers/choice';

import type { PanelComponentEntry } from './presentations';

/** Observation and ObservationComponent both carry these — helpers accept either. */
type Interpretable = Pick<Observation, 'interpretation' | 'referenceRange'>;

/** Minimal display formatting for choice values. Grows with the curated set. */
export function formatChoiceValue(choice: ChoiceValue): string {
  switch (choice.type) {
    case 'quantity': {
      const { value, unit, code } = choice.value;
      return [value, unit ?? code]
        .filter((part) => part !== undefined)
        .join(' ');
    }
    case 'codeableConcept':
      return choice.value.text ?? choice.value.coding?.[0]?.display ?? '';
    case 'string':
    case 'dateTime':
    case 'date':
      return choice.value;
    case 'number':
      return String(choice.value);
    case 'boolean':
      return choice.value ? 'Yes' : 'No';
    default:
      return '';
  }
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** One component's value, whatever `value[x]` it carries (quantity, string, …). */
export function componentValueText(
  component: ObservationComponent,
): string | undefined {
  const choice = getChoiceValue(component, 'value');
  if (!choice || choice.type === 'absent') return undefined;
  // A quantity with a unit but no number is not a value: without this it
  // would format as the bare unit ("degrees").
  if (choice.type === 'quantity' && typeof choice.value.value !== 'number') {
    return undefined;
  }
  return formatChoiceValue(choice) || undefined;
}

function componentLabel(component: ObservationComponent): string | undefined {
  return component.code?.text ?? component.code?.coding?.[0]?.display;
}

/** Boundaries a shared label prefix may be cut on, longest first. */
const LABEL_SEPARATORS = [' - ', ': ', ' '];

/**
 * The prefix every label shares, trimmed back to a separator boundary so
 * elision never cuts mid-word. Empty when there is nothing safe to drop.
 */
function sharedLabelPrefix(labels: readonly string[]): string {
  const [head, ...rest] = labels;
  if (head === undefined || rest.length === 0) return '';
  let prefix = head;
  for (const label of rest) {
    let length = 0;
    while (
      length < prefix.length &&
      length < label.length &&
      prefix[length] === label[length]
    ) {
      length += 1;
    }
    prefix = prefix.slice(0, length);
    if (!prefix) return '';
  }
  const cut = LABEL_SEPARATORS.reduce((best, separator) => {
    const at = prefix.lastIndexOf(separator);
    return at === -1 ? best : Math.max(best, at + separator.length);
  }, 0);
  return prefix.slice(0, cut);
}

/**
 * Panel component labels frequently repeat the panel's own name ("Range of
 * motion - flexion", "Range of motion - abduction", …), which is unreadable
 * stacked four deep in a width-constrained row. Drop the part they all share.
 * Structural only: labels sharing no prefix (BP's Systolic/Diastolic) and
 * anything that would elide to nothing are returned untouched.
 */
function elideSharedPrefix(labels: readonly string[]): string[] {
  const prefix = sharedLabelPrefix(labels);
  if (!prefix) return [...labels];
  const elided = labels.map((label) => label.slice(prefix.length).trim());
  if (elided.some((label) => label === '')) return [...labels];
  return elided.map(sentenceCase);
}

/**
 * Every component the observation carries, labeled from the components
 * themselves. The generic counterpart to `panelComponents`: no presentation,
 * no code table, so panel-shaped observations outside the curated vital-signs
 * set still render their values instead of an em-dash.
 */
export function observationComponentEntries(
  observation: Observation,
): PanelComponentEntry[] {
  // An unlabeled component keeps an empty label rather than being dropped —
  // a strictly-coded component (no display, common in EHR feeds) still has a
  // value worth showing, and losing it is the em-dash bug all over again.
  const labeled = (observation.component ?? []).map((component) => ({
    label: componentLabel(component) ?? '',
    component,
  }));
  const labels = elideSharedPrefix(labeled.map((entry) => entry.label));
  return labeled.map((entry, index) => ({
    component: entry.component,
    label: labels[index] ?? entry.label,
  }));
}

/** A panel's components as one line: "Flexion 95 degrees · Abduction 80 degrees". */
export function componentPanelText(
  observation: Observation,
): string | undefined {
  const parts = observationComponentEntries(observation).flatMap((entry) => {
    const value = componentValueText(entry.component);
    if (!value) return [];
    return [entry.label ? `${entry.label} ${value}` : value];
  });
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

const BODY_STRUCTURE_PREFIX = 'structure of ';
const BODY_STRUCTURE_SUFFIXES = [' region', ' structure'];

/**
 * Display label for a body site. SNOMED body-structure displays wrap the part
 * that matters in boilerplate ("Structure of right shoulder region"), which
 * truncates away the laterality in a narrow row — strip it. Displays without
 * that boilerplate pass through.
 */
export function bodySiteLabel(
  site: CodeableConcept | undefined,
): string | undefined {
  const source = (site?.text ?? site?.coding?.[0]?.display)?.trim();
  if (!source) return undefined;
  let label = source;
  if (label.toLowerCase().startsWith(BODY_STRUCTURE_PREFIX)) {
    label = label.slice(BODY_STRUCTURE_PREFIX.length);
  }
  for (const suffix of BODY_STRUCTURE_SUFFIXES) {
    if (label.toLowerCase().endsWith(suffix)) {
      label = label.slice(0, -suffix.length);
      break;
    }
  }
  return label ? sentenceCase(label) : source;
}

export type InterpretationTone = 'negative' | 'warning';

export type InterpretationFlag = {
  label: string;
  tone: InterpretationTone;
};

/**
 * v3-ObservationInterpretation codes worth flagging. Normal ('N') and
 * unmapped codes render no flag — absence of a flag reads as unremarkable.
 */
const INTERPRETATION_FLAGS: Record<string, InterpretationFlag> = {
  HH: { label: 'Critical high', tone: 'negative' },
  LL: { label: 'Critical low', tone: 'negative' },
  AA: { label: 'Critical', tone: 'negative' },
  H: { label: 'High', tone: 'warning' },
  L: { label: 'Low', tone: 'warning' },
  A: { label: 'Abnormal', tone: 'warning' },
};

export function interpretationFlag(
  data: Interpretable,
): InterpretationFlag | undefined {
  const code = data.interpretation?.[0]?.coding?.[0]?.code;
  return code ? INTERPRETATION_FLAGS[code] : undefined;
}

/**
 * Most severe flag across a panel's components — lets a composed value
 * ("158/96 mmHg") carry its components' abnormality when the panel root has
 * no interpretation of its own. Severity: negative (critical) > warning.
 */
export function worstComponentFlag(
  data: Observation,
): InterpretationFlag | undefined {
  let worst: InterpretationFlag | undefined;
  for (const component of data.component ?? []) {
    const flag = interpretationFlag(component);
    if (!flag) continue;
    if (!worst || (flag.tone === 'negative' && worst.tone !== 'negative')) {
      worst = flag;
    }
  }
  return worst;
}

function quantityText(quantity: Quantity | undefined): string | undefined {
  if (quantity?.value === undefined) return undefined;
  return String(quantity.value);
}

/** "3.5–5.5 mmol/L" from the first reference range; explicit text wins. */
export function referenceRangeText(data: Interpretable): string | undefined {
  const range = data.referenceRange?.[0];
  if (!range) return undefined;
  if (range.text) return range.text;
  const low = quantityText(range.low);
  const high = quantityText(range.high);
  if (low === undefined && high === undefined) return undefined;
  const unit = range.low?.unit ?? range.high?.unit ?? '';
  const bounds =
    low !== undefined && high !== undefined
      ? `${low}–${high}`
      : low !== undefined
        ? `≥${low}`
        : `≤${high}`;
  return unit ? `${bounds} ${unit}` : bounds;
}

/**
 * Deterministic short date for display ("Jun 12, 2026"); time dropped.
 * Rendered in UTC: date-only FHIR values parse as UTC midnight, and a local
 * formatter would show the previous day west of Greenwich.
 */
export function formatFhirDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Deterministic short date + time for instants ("Jun 12, 2026, 14:03 UTC").
 * UTC for the same reason as formatFhirDate, and because the values it renders
 * (meta.lastUpdated and friends) are server timestamps, not clinic-local ones.
 */
export function formatFhirDateTime(value: string | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })} UTC`;
}

/** The observation's clinically relevant time: effective first, issued as fallback. */
export function observationDateText(data: Observation): string | undefined {
  return formatFhirDate(
    data.effectiveDateTime ?? data.effectivePeriod?.start ?? data.issued,
  );
}
